import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2, ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { TOURS, YACHTS, DEPOSIT } from "@/data/content";
import { API, http } from "@/lib/api";
import { useBooking } from "./BookingContext";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.25em] text-zinc-400 mb-2">{label}</span>
    {children}
  </label>
);

const today = () => new Date().toISOString().slice(0, 10);
const empty = { date: "", time: "", guests: 2, name: "", email: "", phone: "", message: "" };

export const BookingForm = () => {
  const { t, lang } = useLang();
  const { experience, setExperience } = useBooking();
  const [f, setF] = useState(empty);
  const [pay, setPay] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const isYacht = YACHTS.some((y) => y.id === experience);
  const deposit = isYacht ? DEPOSIT.yacht : DEPOSIT.car;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post(`${API}/bookings`, { ...f, guests: Number(f.guests), experience_id: experience, language: lang });
      if (pay) {
        const res = await http.post(`${API}/payments/checkout`, { booking_id: data?.id, origin_url: window.location.origin });
        if (res.data?.checkout_url) { window.location.href = res.data.checkout_url; return; }
      }
      setDone(true);
      setF(empty);
      toast.success(t.booking.ok);
    } catch (err) {
      toast.error(t.booking.err);
    }
    setBusy(false);
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="glass-card p-10 md:p-14 text-center" data-testid="booking-success">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-[#C5A059] text-[#E5C158]"><Check /></span>
        <h3 className="mt-8 font-display text-5xl text-[#F9F9F9]">{t.booking.okTitle}</h3>
        <p className="mt-4 text-zinc-300">{t.booking.ok}</p>
        <button className="btn-ghost mt-10" onClick={() => setDone(false)} data-testid="booking-again-btn">{t.booking.again}</button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="glass-card p-6 sm:p-8 md:p-10 grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="booking-form">
      <div className="sm:col-span-2">
        <Field label={t.booking.experience}>
          <select className="lux-input" value={experience} onChange={(e) => setExperience(e.target.value)} data-testid="booking-experience-select">
            <optgroup label={t.booking.groupTours}>
              {TOURS.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
              <option value="custom">{t.booking.custom}</option>
            </optgroup>
            <optgroup label={t.booking.groupYachts}>
              {YACHTS.map((x) => <option key={x.id} value={x.id}>{x.title[lang]}</option>)}
            </optgroup>
          </select>
        </Field>
      </div>
      <Field label={t.booking.date}><input required type="date" min={today()} className="lux-input" value={f.date} onChange={set("date")} data-testid="booking-date-input" /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label={t.booking.time}><input type="time" className="lux-input" value={f.time} onChange={set("time")} data-testid="booking-time-input" /></Field>
        <Field label={t.booking.guests}><input required type="number" min={1} max={60} className="lux-input" value={f.guests} onChange={set("guests")} data-testid="booking-guests-input" /></Field>
      </div>
      <Field label={t.booking.name}><input required minLength={2} className="lux-input" value={f.name} onChange={set("name")} data-testid="booking-name-input" /></Field>
      <Field label={t.booking.email}><input required type="email" className="lux-input" value={f.email} onChange={set("email")} data-testid="booking-email-input" /></Field>
      <div className="sm:col-span-2"><Field label={t.booking.phone}><input required minLength={6} className="lux-input" value={f.phone} onChange={set("phone")} data-testid="booking-phone-input" /></Field></div>
      <div className="sm:col-span-2"><Field label={t.booking.message}><textarea rows={3} className="lux-input resize-none" value={f.message} onChange={set("message")} data-testid="booking-message-input" /></Field></div>
      <label className="sm:col-span-2 flex items-start gap-4 cursor-pointer rounded-2xl border border-[#C5A059]/25 bg-[#C5A059]/5 p-5 transition-colors hover:border-[#C5A059]/60" data-testid="booking-deposit-toggle">
        <input type="checkbox" checked={pay} onChange={(e) => setPay(e.target.checked)} className="mt-1 h-4 w-4 accent-[#C5A059]" data-testid="booking-deposit-checkbox" />
        <span>
          <span className="block text-[#F9F9F9]">{t.booking.deposit} <strong className="text-[#E5C158] font-semibold" data-testid="booking-deposit-amount">{deposit} €</strong></span>
          <span className="block text-sm text-zinc-400 mt-1">{t.booking.depositNote}</span>
        </span>
      </label>
      <button type="submit" disabled={busy} className="btn-gold sm:col-span-2 justify-center py-4 disabled:opacity-60" data-testid="booking-submit-button">
        {busy ? <><Loader2 size={16} className="animate-spin" />{t.booking.sending}</> : <>{pay ? t.booking.submitPay : t.booking.submit}<ArrowUpRight size={16} /></>}
      </button>
    </form>
  );
};
